/**
 * THE PLAN CATALOG — what each paid plan costs, in one place.
 *
 * Cursor's ladder (Oct 2026): Pro $20, Pro+ $60, Ultra $200 a month for one
 * person, Teams $40 a seat, with about 20% off for paying yearly. The Stripe
 * setup script creates prices from these numbers and the SPA shows them, so
 * what a subscriber sees and what Stripe charges cannot drift apart.
 *
 * EVERY PLAN INCLUDES AI CREDITS WORTH ITS PRICE. A $20 plan carries $20 of
 * model usage a month, metered at the vendors' own prices (core's meter: each
 * call's cost at $4 per 1M managed tokens). Core's `TIER_BUDGETS` holds that
 * allowance in tokens; `test/plan-credits.test.ts` pins the two together, so
 * a price change that is not matched by its allowance fails CI rather than
 * quietly selling a plan for more or less than it includes.
 *
 * Changing a price here does NOT change an existing Stripe price — Stripe
 * prices are immutable. Run the setup script again: it creates a new price
 * and moves the lookup key to it (existing subscribers keep their old price).
 *
 * Imported by the server, the SPA bundle and scripts/stripe-setup.ts, so it
 * has no dependencies.
 */
export const PLAN_IDS = ["pro", "pro_plus", "ultra", "team"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface PlanPrice {
  readonly id: PlanId;
  readonly label: string;
  /** One line for the plan card. */
  readonly blurb: string;
  /** Whole US dollars a month (per seat for Teams). */
  readonly monthlyUsd: number;
  /** Whole US dollars a year (per seat for Teams): 12 months less 20%. */
  readonly yearlyUsd: number;
  /** Billed per seat. */
  readonly perSeat: boolean;
  readonly minSeats: number;
  /** Usage relative to Pro. */
  readonly usage: string;
  /** AI credits included each month, in US dollars (per seat for Teams): the monthly price. */
  readonly creditsUsd: number;
}

const yearly = (monthly: number): number => Math.round(monthly * 12 * 0.8);

export const PLAN_CATALOG: readonly PlanPrice[] = [
  { id: "pro", label: "Pro", blurb: "For building every day.", monthlyUsd: 20, yearlyUsd: yearly(20), perSeat: false, minSeats: 1, usage: "Pro usage", creditsUsd: 20 },
  { id: "pro_plus", label: "Pro+", blurb: "Three times Pro's credits.", monthlyUsd: 60, yearlyUsd: yearly(60), perSeat: false, minSeats: 1, usage: "3x Pro", creditsUsd: 60 },
  { id: "ultra", label: "Ultra", blurb: "Ten times Pro's credits.", monthlyUsd: 200, yearlyUsd: yearly(200), perSeat: false, minSeats: 1, usage: "10x Pro", creditsUsd: 200 },
  { id: "team", label: "Teams", blurb: "Shared billing, SSO and a team memory.", monthlyUsd: 40, yearlyUsd: yearly(40), perSeat: true, minSeats: 3, usage: "2x Pro per seat", creditsUsd: 40 },
];

export function planPrice(id: PlanId): PlanPrice {
  const p = PLAN_CATALOG.find((x) => x.id === id);
  if (!p) throw new Error(`unknown plan ${id}`);
  return p;
}

/** "$16/mo" — a yearly price shown per month, as Cursor shows it. */
export function monthlyEquivalent(p: PlanPrice, interval: "month" | "year"): number {
  return interval === "year" ? Math.round((p.yearlyUsd / 12) * 100) / 100 : p.monthlyUsd;
}
